// src/library/chat/chat-rendered.tsx
import {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
// @ts-ignore
import Prism from "prismjs";

import {
  useActionInvoke,
  useAgentApiAgentList,
  useAgentApiConversationWithMessages,
  useConversationMetadataAggregated,
  Loadable,
  ObjRecord,
} from "@heartcheck/shared-react";

import { Flex, Loader, Text, View, useTheme } from "@aws-amplify/ui-react";

import {
  AgentChatMessage,
  AgentGraphQLBlock,
  AgentInnerDialogBlock,
  AgentJSONBlock,
  GraphQLResultBlock,
  UserChatMessage,
} from "./chat-items";

import { Agent, ConversationEvent } from "@heartcheck/shared";

function EnterUserSection() {
  const { tokens } = useTheme();
  return (
    <View
      padding={10}
      width="100%"
      backgroundColor={tokens.colors.brand.primary[10]}
    >
      <Text textTransform="capitalize" textAlign="center">
        You
      </Text>
    </View>
  );
}

function EnterAgentSection({ name }: { name?: string }) {
  const { tokens } = useTheme();
  return (
    <View
      padding={10}
      width="100%"
      backgroundColor={tokens.colors.brand.primary[10]}
    >
      <Text textTransform="capitalize" textAlign="center">
        {name || "Agent"}
      </Text>
    </View>
  );
}

type Section = "user" | "agent";

// Treat only actual agent *messages* as section-switchers (not metadata/partials)
const isRealAgentMessage = (e: ConversationEvent) =>
  e?.sender === "agent" && !!(e?.event?.message || e?.event?.actionResult);

/** Safely read an optional carryover agent id without depending on Conversation type */
function getCarryoverAgentId(conversation: unknown): string | undefined {
  const c = conversation as any;
  const maybeId = c?.carryoverSystem?.agentId;
  return typeof maybeId === "string" && !!maybeId ? maybeId : undefined;
}

export function ChatRendered({
  events: externalEvents,
  onRenderedCount,
}: {
  events?: ConversationEvent[];
  onRenderedCount?: (n: number) => void;
}) {
  const params = useParams<{ chatId: string }>();
  const chatId = params?.chatId;
  const chatIdSafe = chatId ?? "";

  // Internal hook for conversation/events; parent may optionally supply events.
  const conversationEventsRaw = useAgentApiConversationWithMessages(chatIdSafe);

  const conversationEvents = (conversationEventsRaw ??
    { conversation: undefined, events: [] }) as {
    conversation?: unknown;
    events?: ConversationEvent[];
  };

  const { conversation } = conversationEvents;

  const parentProvidedEvents = useMemo(
    () => Array.isArray(externalEvents),
    [externalEvents]
  );

  const internalEvents: ConversationEvent[] = useMemo(
    () => conversationEvents.events ?? [],
    [conversationEvents.events]
  );

  const parentEvents: ConversationEvent[] = useMemo(
    () => (parentProvidedEvents ? (externalEvents as ConversationEvent[]) : []),
    [parentProvidedEvents, externalEvents]
  );

  const events: ConversationEvent[] = useMemo(() => {
    const byId = new Map<string, ConversationEvent>();
    (internalEvents || []).forEach((e) => e?.id && byId.set(e.id, e));
    (parentProvidedEvents ? parentEvents : []).forEach(
      (e) => e?.id && byId.set(e.id, e)
    );
    return Array.from(byId.values()).sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [internalEvents, parentProvidedEvents, parentEvents]);

  useEffect(() => {
    onRenderedCount?.(events.length);
  }, [events.length, onRenderedCount]);

  // ✅ Properly unwrap useAgentApiAgentList hybrid result
  const agentListResult: any = useAgentApiAgentList();
  const agentList: Loadable<ObjRecord<Agent>> =
    agentListResult?.state ??
    agentListResult ??
    Loadable.unloaded<ObjRecord<Agent>>();

  const chatBottomRef = useRef<HTMLDivElement>(null);
  const chatRootRef = useRef<HTMLDivElement>(null);

  const actionInvoke =
    useActionInvoke(chatIdSafe) ?? ({ invokeAction: () => {} } as {
      invokeAction: (eventId: string, resource: string) => void;
    });
  const { invokeAction } = actionInvoke;

  // Work out a provisional active agent (used to filter the metadata subscription)
  const lastEventAgentId = events.length
    ? events[events.length - 1]?.agentId
    : undefined;
  const carryoverAgentId = getCarryoverAgentId(conversation);
  const provisionalActiveId: string | undefined =
    carryoverAgentId ?? (conversation as any)?.agent ?? lastEventAgentId;

  // Subscribe to aggregated metadata (don’t filter by agent; let early hints show)
  const aggregatedMetaRaw = useConversationMetadataAggregated(chatIdSafe);
  const convMeta =
    aggregatedMetaRaw && typeof aggregatedMetaRaw === "object"
      ? aggregatedMetaRaw.meta
      : undefined;
  const metaAgentId =
    aggregatedMetaRaw && typeof aggregatedMetaRaw === "object"
      ? aggregatedMetaRaw.agentId
      : undefined;

  // Final active agent to display: prefer what metadata reports, else our provisional guess
  const activeAgentId: string | undefined = useMemo(() => {
    return metaAgentId ?? provisionalActiveId;
  }, [metaAgentId, provisionalActiveId]);

  const agentFromList =
    agentList.isLoaded() && agentList.value && activeAgentId
      ? agentList.value.get(activeAgentId)
      : undefined;

  const agentName =
    (agentFromList && agentFromList.name) || activeAgentId || "Agent";

  useEffect(() => {
    const el = chatRootRef.current as any;
    const raf = requestAnimationFrame(() => {
      if (el && typeof Prism?.highlightAllUnder === "function") {
        Prism.highlightAllUnder(el);
      } else if (typeof Prism?.highlightAll === "function") {
        Prism.highlightAll();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [events.length]);

  const [stickToBottom, setStickToBottom] = useState(true);
  const bodyElRef = useRef<HTMLElement | null>(null);

  const updateStickinessFromScroll = useCallback(() => {
    const el = bodyElRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setStickToBottom(distanceFromBottom < 120);
  }, []);

  useEffect(() => {
    const el = document.querySelector(".body") as HTMLElement | null;
    bodyElRef.current = el;
    if (!el) return;

    updateStickinessFromScroll();

    el.addEventListener("scroll", updateStickinessFromScroll, { passive: true });
    return () => el.removeEventListener("scroll", updateStickinessFromScroll);
  }, [updateStickinessFromScroll]);

  const agentPartialMessage = convMeta?.agentPartialMessage;
  useEffect(() => {
    if (stickToBottom) {
      chatBottomRef.current?.scrollIntoView({
        behavior: "auto",
        block: "nearest",
      });
    }
  }, [events.length, agentPartialMessage, stickToBottom]);

  const typingStartRef = useRef<Map<string, number>>(new Map());
  const getTypingStart = useCallback((eventId: string, seed: number) => {
    const map = typingStartRef.current;
    const existing = map.get(eventId);
    if (typeof existing === "number") return existing;
    map.set(eventId, seed);
    return seed;
  }, []);

  const firstAgentMessageId = useMemo(() => {
    const first = events.find(
      (e) =>
        e.sender === "agent" &&
        !!e.event &&
        !!e.event.message &&
        isRealAgentMessage(e)
    );
    return first?.id;
  }, [events]);

  let lastSection: Section | null = null;
  const renderedChat: ReactNode[] = [];

  const initialTimestamp =
    events.length > 0 ? new Date(events[0].timestamp).getTime() : Date.now();
  let lastEffectEndTime = initialTimestamp;

  events.forEach((event) => {
    const eventTime = new Date(event.timestamp).getTime();
    if (eventTime > lastEffectEndTime) lastEffectEndTime = eventTime;

    let messageSize = 0;

    const isLambdaAgent =
      event.sender === "user" &&
      typeof event.userId === "string" &&
      event.userId.startsWith("AppsyncAgentFunction-");

    const typingSeedForEvent = getTypingStart(event.id, lastEffectEndTime);

    if (event.sender === "user") {
      if (isLambdaAgent) {
        if (lastSection !== "agent") {
          renderedChat.push(
            <EnterAgentSection name={agentName} key={`agent-section-${event.id}`} />
          );
          lastSection = "agent";
        }
        const text =
          event.event.message ||
          event.event.innerDialog ||
          event.event.actionResult;
        if (text) {
          renderedChat.push(
            <AgentChatMessage
              text={text}
              event={event}
              lastEventTime={typingSeedForEvent}
              key={`agent-msg-${event.id}`}
            />
          );
        }
        return;
      }

      if (lastSection !== "user") {
        renderedChat.push(<EnterUserSection key={`user-section-${event.id}`} />);
        lastSection = "user";
      }

      if (event.event.message) {
        renderedChat.push(
          <UserChatMessage
            text={event.event.message}
            event={event}
            lastEventTime={typingSeedForEvent}
            key={`user-msg-${event.id}`}
          />
        );
      }

      if (event.event.actionResult) {
        renderedChat.push(
          <GraphQLResultBlock
            text={event.event.actionResult}
            event={event}
            lastEventTime={typingSeedForEvent}
            key={`user-action-${event.id}`}
          />
        );
      }
    } else if (event.sender === "agent") {
      const shouldEnterAgent =
        isRealAgentMessage(event) && lastSection !== "agent";
      if (shouldEnterAgent) {
        renderedChat.push(
          <EnterAgentSection name={agentName} key={`agent-section-${event.id}`} />
        );
        lastSection = "agent";
      }

      const isFirstWelcome =
        event.id === firstAgentMessageId && !!event.event.message;

      if (event.event.message) {
        const parts = event.event.message.split("```");
        let localTime = typingSeedForEvent;

        parts.forEach((part, partIndex) => {
          const stableKey = `${event.id}::part-${partIndex}`;

          if (isFirstWelcome && partIndex % 2 === 0) {
            renderedChat.push(
              <View
                textAlign="left"
                paddingLeft={20}
                paddingRight={20}
                key={`agent-welcome-raw-${stableKey}`}
              >
                <View lineHeight={2}>
                  <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {part}
                  </Text>
                </View>
              </View>
            );
            localTime += part.length * 5;
            messageSize += part.length;
            return;
          }

          if (partIndex % 2 === 0) {
            renderedChat.push(
              <AgentChatMessage
                text={part}
                event={event}
                lastEventTime={localTime}
                key={`agent-msg-${stableKey}`}
              />
            );
            localTime += part.length * 5;
            messageSize += part.length;
          } else {
            renderedChat.push(
              <AgentJSONBlock
                text={part}
                event={event}
                lastEventTime={localTime}
                key={`agent-json-${stableKey}`}
              />
            );
          }
        });
      } else if (event.event.actionRequested) {
        if (event.event.actionResult) {
          renderedChat.push(
            <GraphQLResultBlock
              text={event.event.actionResult}
              event={event}
              lastEventTime={typingSeedForEvent}
              key={`agent-result-${event.id}`}
            />
          );
        } else {
          renderedChat.push(
            <AgentGraphQLBlock
              text={event.event.actionRequested}
              event={event}
              lastEventTime={typingSeedForEvent}
              invoke={() => invokeAction(event.id, event.event.actionRequested)}
              key={`agent-invoke-${event.id}`}
            />
          );
        }
      } else if (event.event.innerDialog) {
        renderedChat.push(
          <AgentInnerDialogBlock
            text={event.event.innerDialog}
            event={event}
            lastEventTime={typingSeedForEvent}
            key={`agent-inner-${event.id}`}
          />
        );
        messageSize += event.event.innerDialog.length;
      }
    }

    lastEffectEndTime += messageSize * 5;
  });

  const noChatId = !chatId;

  return (
    <View
      ref={chatRootRef}
      style={{
        width: "100%",
        maxWidth: "700px",
        marginLeft: "0",
        marginRight: "auto",
        padding: "0 16px",
        minHeight: 0,
      }}
    >
      <Flex direction="column" justifyContent="flex-end">
        {noChatId ? <Loader /> : renderedChat}
        <div ref={chatBottomRef} />
      </Flex>
    </View>
  );
}
